import moment from "moment";
import React from "react"
import { CellProps } from "react-table";
import Breadcrumbs from "~/components/Breadcrumbs/Breadcrumbs";
import CobuTableAdvanced from "~/components/CobuTableAdvanced/CobuTableAdvanced";
import Spinner from "~/components/Spinner/Spinner";
import CobuConstants from "~/helpers/cobuConstants";
import usePinnedPosts from "~/hooks/post/usePinnedPosts";
import { PinPost } from "~/types/postComment";
import styles from './PinnedPostManagement.module.css'
import Spacer from "~/components/Spacer/Spacer";
import { useHistory } from "react-router-dom";
import MoreActions from "~/components/MoreActions/MoreActions";


const PinnedPostManagement = () => {

    const { pinPosts, loadingConfig, unPinComment, unPinCommentAndHide, bulkUnpin } = usePinnedPosts()
    const history = useHistory();
    const [selected, setSelected] = React.useState<Set<string>>(new Set())

    // Selects all rows, not search-filtered ones. This table has no search.
    const allSelected = pinPosts.length > 0 && selected.size === pinPosts.length

    const toggleSelection = (uuid: string) => {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(uuid)) {
                next.delete(uuid)
            } else {
                next.add(uuid)
            }
            return next
        })
    }

    const toggleSelectAll = () => {
        setSelected(allSelected ? new Set() : new Set(pinPosts.map(post => post.uuid)))
    }

    const runBulkUnpin = async (hidden: boolean) => {
        const uuids = pinPosts.filter(post => selected.has(post.uuid)).map(post => post.uuid)
        const confirmation = hidden
            ? CobuConstants.pinnedPostManagement.bulkHideConfirm
            : CobuConstants.pinnedPostManagement.bulkUnpinConfirm
        if (!window.confirm(`${uuids.length} post(s). ${confirmation}`)) return
        await bulkUnpin(uuids, hidden)
        setSelected(new Set())
    }

    /* UI Components */

    const pinnedPostTableConfig = [
        {
            id: 'select',
            disableSortBy: true,
            Header: () => (
                <input
                    type='checkbox'
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={pinPosts.length === 0}
                />
            ),
            Cell: (cell: CellProps<PinPost>) => (
                <input
                    type='checkbox'
                    checked={selected.has(cell.row.original.uuid)}
                    onChange={() => toggleSelection(cell.row.original.uuid)}
                />
            )
        },
        {
            Header: 'BUILDING',
            accessor: 'building.name'
        },
        {
            Header: 'CONTENTS',
            accessor: 'contents',
            Cell: (cell: CellProps<PinPost>) => {
                return <div
                    style={{ whiteSpace: 'normal' }}
                    className='default-text'>
                    {cell.row.original.contents}
                </div>
            },
        },
        {
            Header: 'CREATED',
            accessor: 'createdAt',
            Cell: (cell: CellProps<PinPost>) => (
                <div>
                    {moment(cell.row.original.createdAt)
                        .local()
                        .format('MM/DD/YYYY')}
                </div>
            )
        },
        {
            id: 'pin/unpin',
            Cell: (cell: CellProps<PinPost>) => (
                <MoreActions
                    title={CobuConstants.pinnedPostManagement.actions}
                    options={[
                        {
                            label: CobuConstants.pinnedPostManagement.removePin,
                            action: () => unPinComment(cell.row.original)
                        },
                        {
                            label: CobuConstants.pinnedPostManagement.removePinAndHide,
                            action: () => unPinCommentAndHide(cell.row.original)
                        }
                    ]}
                />
            )
        }
    ]

    const getCobuTableInstance = (data: PinPost[], placeholder: string) => {
        return (
            <CobuTableAdvanced
                columns={pinnedPostTableConfig}
                data={data}
                noData={{
                    label: placeholder,
                    action: undefined,
                    image: undefined
                }}
                actions={[bulkActions(), createPostAction()]}
                actionsStyle={{ top: '130px' }}
            />
        );
    };

    const bulkActions = () => {
        if (selected.size === 0) return null;

        return (
            <MoreActions
                title={`${CobuConstants.pinnedPostManagement.actions} (${selected.size})`}
                options={[
                    {
                        label: CobuConstants.pinnedPostManagement.removePin,
                        action: () => runBulkUnpin(false)
                    },
                    {
                        label: CobuConstants.pinnedPostManagement.removePinAndHide,
                        action: () => runBulkUnpin(true)
                    }
                ]}
            />
        );
    };

    const createPostAction = () => {
        return (
            <button
                className='btn-primary btn-standard bold'
                onClick={() => {
                    history.push(`/building/pinned-post-management/post-management`);
                }}
            >
                {CobuConstants.pinnedPostManagement.createPost}
            </button>
        );
    };

    if (loadingConfig.flag) return <div className={styles.loadingContainer}>
        <Spinner />
        <h3 style={{ textAlign: 'center' }}>{loadingConfig.comment}</h3>
    </div>

    return (
        <div className='content-container'>
            <Breadcrumbs />
            <Spacer height={20} />
            <h1>{CobuConstants.pinnedPostManagement.title}</h1>
            <Spacer height={20} />
            {getCobuTableInstance(pinPosts, CobuConstants.pinnedPostManagement.noPinnedPosts)}
        </div>
    )
}

export default PinnedPostManagement;